//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { InstagramStoryGroupClipCustomizationType } from '@models/IScheduledInstagramStoryGroup';
import { InstagramReelsCreativeNotesType } from '@universal-scheduling/components/scheduler/post-details-forms/instagram/reels-customizations/InstagramReelsPostDetailsCustomizationsConstants';
import {
  IInstagramFeedAutomaticFormState,
  IInstagramFeedManualFormState,
  IInstagramReelsAutomaticFormState,
  IInstagramReelsManualFormState,
  IInstagramStoryAutomaticFormState,
  IInstagramStoryManualFormState,
} from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { parseHashtags } from '@utils/stringUtils';

// TODO PD-243507: Set automatic or manual default form state based on user settings
export const DEFAULT_INSTAGRAM_POST_DETAILS_AUTOMATIC_FORM_STATE: IInstagramFeedAutomaticFormState =
  {
    postNow: false,
    postType: UniversalPostType.InstagramFeedAutomaticPost,
  };

export const DEFAULT_INSTAGRAM_POST_DETAILS_MANUAL_FORM_STATE: IInstagramFeedManualFormState =
  {
    postNow: false,
    postType: UniversalPostType.InstagramFeedManualPost,
  };

export const DEFAULT_INSTAGRAM_REELS_DETAILS_AUTOMATIC_FORM_STATE: IInstagramReelsAutomaticFormState =
  {
    postNow: false,
    postType: UniversalPostType.InstagramReelsAutomaticPost,
    shareToFeed: true,
  };

export const DEFAULT_INSTAGRAM_REELS_DETAILS_MANUAL_FORM_STATE: IInstagramReelsManualFormState =
  {
    postNow: false,
    postType: UniversalPostType.InstagramReelsManualPost,
    shareToFeed: true,
  };

export const DEFAULT_INSTAGRAM_STORY_DETAILS_AUTOMATIC_FORM_STATE: IInstagramStoryAutomaticFormState =
  {
    postNow: false,
    postType: UniversalPostType.InstagramStoryGroupAutomaticPost,
  };

export const DEFAULT_INSTAGRAM_STORY_DETAILS_MANUAL_FORM_STATE: IInstagramStoryManualFormState =
  {
    postNow: false,
    postType: UniversalPostType.InstagramStoryGroupPost,
  };

export const LOCATION_SECTION_PLACEHOLDER = {
  Auto: 'Search for a location',
  Manual: 'Unavailable when posting manually',
} as const;

export const LOCATION_SECTION_TOOLTIP =
  'In order to tag locations, you must set the post method to automatically post to Instagram.';

export const MAX_IG_HASHTAG_COUNT = 30;
export const MAX_IG_CHARACTER_COUNT = 2200;
export const MAX_IG_COLLABORATOR_COUNT = 3;

export const InstagramPostDetailsFormFieldErrorText = {
  TextTooLong: `Please enter ${MAX_IG_CHARACTER_COUNT} characters or less.`,
  TooManyCollaborators: `Please include ${MAX_IG_COLLABORATOR_COUNT} collaborators or less.`,
  TooManyHashtags: `Please include ${MAX_IG_HASHTAG_COUNT} hashtags or less.`,
} as const;

// Setting a default limit of 30 hashtags
export const TOP_INSTAGRAM_HASHTAG_RECOMMENDATIONS_LIMIT = 30;

export const filterRecommendedHashtags = (
  recommendedHashtags: string[],
  caption: string | undefined,
  firstComment: string | undefined,
) => {
  const captionHashtags = parseHashtags(caption ?? '').map((tag) =>
    tag.toLowerCase(),
  );
  const firstCommentHashtags = parseHashtags(firstComment ?? '').map((tag) =>
    tag.toLowerCase(),
  );

  const existingHashtags = [...captionHashtags, ...firstCommentHashtags];

  return recommendedHashtags.map((tag) => ({
    shouldShow: !existingHashtags.includes(tag),
    value: tag,
  }));
};

export const PostDetailsTaggedUserSectionPlaceholder = {
  Auto: 'Click on the photo to tag public users.',
  IsPosted: "Tagged users can't be changed after a post has been published.",
  Manual: 'Unavailable when posting manually.',
  NoAccount: 'No public users tagged.',
  NoMedia: 'Select an image to tag public users.',
  Video: 'Unavailable for video posts.',
} as const;

export const INSTAGRAM_TAGGED_USERS_TOOLTIP =
  "Users that will be tagged on your post's media.";

export const INSTAGRAM_COLLABORATORS_TOOLTIP =
  'Invite Instagram users to co-author this post as collaborators. Once they accept your invite, this post will be shown to their followers.';

export const INSTAGRAM_META_TAGS_TOOLTIP =
  "Products from your Meta product catalog that will appear on your post's media. When selected, the user will go to the product's page on your Instagram shop.";

export const PostDetailsTaggedProductSectionPlaceholder = {
  Auto: 'Click on the photo to add Instagram Shop Tags.',
  IsPosted:
    "Instagram Shop Tags can't be changed after a post has been published.",
  Manual: 'Unavailable when posting manually.',
  NoAccount: 'No Instagram Shop Tags.',
  NoMedia: 'Select media to add Instagram Shop Tags.',
} as const;

export const InstagramStoryGroupClipCustomizationTypeLabel: Record<
  InstagramStoryGroupClipCustomizationType,
  string
> = {
  [InstagramStoryGroupClipCustomizationType.SwipeUpUrl]: 'Link',
  [InstagramStoryGroupClipCustomizationType.TextOverlay]: 'Text Overlay',
  [InstagramStoryGroupClipCustomizationType.Location]: 'Location',
  [InstagramStoryGroupClipCustomizationType.AtMention]: '@ Mention',
  [InstagramStoryGroupClipCustomizationType.Hashtag]: 'Hashtag',
  [InstagramStoryGroupClipCustomizationType.Poll]: 'Poll',
  [InstagramStoryGroupClipCustomizationType.Question]: 'Question',
  [InstagramStoryGroupClipCustomizationType.Gif]: 'GIF',
  [InstagramStoryGroupClipCustomizationType.Other]: 'Other',
} as const;

export const storyCustomizationPlaceholderText: Record<
  InstagramStoryGroupClipCustomizationType,
  string
> = {
  [InstagramStoryGroupClipCustomizationType.SwipeUpUrl]:
    'Add a link to your story',
  [InstagramStoryGroupClipCustomizationType.Hashtag]:
    'Add a hashtag to your story',
  [InstagramStoryGroupClipCustomizationType.TextOverlay]:
    'Add text to your story',
  [InstagramStoryGroupClipCustomizationType.Location]:
    'Add a location to your story',
  [InstagramStoryGroupClipCustomizationType.AtMention]:
    'Mention a username in your story',
  [InstagramStoryGroupClipCustomizationType.Poll]: 'Add a Poll to your story',
  [InstagramStoryGroupClipCustomizationType.Question]:
    'Add a question to your story',
  [InstagramStoryGroupClipCustomizationType.Gif]:
    'Describe a GIF to add to your story',
  [InstagramStoryGroupClipCustomizationType.Other]:
    'Describe a sticker to add to your story',
} as const;

export const UNAVAILABLE_FOR_AUTOMATIC_TEXT =
  'Unavailable when posting automatically.';
export const STORY_CLIP_NO_MEDIA_TOOLTIP = `Select media to add links and notes to your Story.`;
export const STORY_CLIP_TOOLTIP =
  'Select a clip to add a link or notes to your story.';
export const STORY_NOTES_TOOLTIP = `Notes help you keep track of any text overlays, hashtags, mentions, locations tags, or more that you'll want to add to your story. We'll make it easy to copy and paste your notes when it's time to post.`;

export const STORY_URL_SECTION_TOOLTIP = `Add a link to your Story. Link to a Showroom to make your Story shoppable!`;

export const PostDetailsPublishingStrategyTooltip = `Automatic posting will publish this post on your behalf. \n\n Turn this off if you would prefer to receive a mobile notification at the scheduled time reminding you to post.`;

export interface IReelsCreativeNote {
  index?: number;
  type: InstagramReelsCreativeNotesType;
  value: string;
}

export const INSTAGRAM_USER_IFRAME_SHOW_DELAY = 500;

export const INSTAGRAM_COVER_UPLOAD_LIMIT = 8 * 1024 * 1024; // 8MB

export const INSTAGRAM_COVER_UPLOAD_SIZE_ERROR =
  'Custom cover images for Instagram must be less than 8MB.';

// Instagram Reels limits to be eligible to be published to Reels tab
export const REELS_TAB_MIN_VIDEO_DURATION_MILLIS = 5000; // 5 secs in ms
export const REELS_TAB_MAX_VIDEO_DURATION_MILLIS = 90000; // 90 secs in ms
export const REELS_TAB_IDEAL_ASPECT_RATIO = 9 / 16;

// Hard limits for Instagram Reels media set by Instagram API
export const REELS_MIN_VIDEO_DURATION_MILLIS = 3000; // 3 secs in ms
export const REELS_MAX_VIDEO_DURATION_MILLIS = 900000; // 15 minutes in ms
export const REELS_MIN_ASPECT_RATIO = 0.01 / 1;
export const REELS_MAX_ASPECT_RATIO = 10 / 1;
export const REELS_MAX_VIDEO_SIZE = 1 * 1024 * 1024 * 1024; // 1gb in bytes
export const STORY_MIN_VIDEO_DURATION_MILLIS = 3000; // 3 secs in ms
export const STORY_MAX_VIDEO_DURATION_MILLIS = 60000; // 1 minute in ms
export const STORY_MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100mb in bytes
export const STORY_MAX_IMAGE_SIZE = 8 * 1024 * 1024; // 8mb in bytes
export const RECOMMENDED_IG_STORY_ASPECT_RATIO = 9 / 16;

export const INSTAGRAM_FEED_MAX_VIDEO_DURATION_MILLIS = 60000;
export const INSTAGRAM_FEED_MIN_VIDEO_DURATION_MILLIS = 3000;
export const INSTAGRAM_FEED_MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;
export const INSTAGRAM_FEED_MAX_IMAGE_SIZE_BYTES = 8 * 1024 * 1024;
export const MAX_IG_MESSAGE_LENGTH = 2200;
export const MAX_IG_GALLERY_POST_MEDIA_COUNT = 10;

const MIN_IG_ASPECT_RATIO = 1012 / 1350;
const MAX_IG_ASPECT_RATIO = 1.91 / 1;

export const isValidIgImageAspectRatio = (aspectRatio: number): boolean => {
  return (
    aspectRatio >= MIN_IG_ASPECT_RATIO && aspectRatio <= MAX_IG_ASPECT_RATIO
  );
};

const MIN_IG_VIDEO_ASPECT_RATIO = 9 / 16;
const MAX_IG_VIDEO_ASPECT_RATIO = 1.91 / 1;

export const isValidIgVideoAspectRatio = (aspectRatio: number): boolean => {
  return (
    aspectRatio >= MIN_IG_VIDEO_ASPECT_RATIO &&
    aspectRatio <= MAX_IG_VIDEO_ASPECT_RATIO
  );
};
